import { api } from "./api";
import { Service, PagedResponse, DurationOption } from "../types";

// Allowed durations from backend enum (max 72 hours)
export const ALLOWED_DURATIONS = [
  1, 2, 4, 6, 8, 12, 16, 20, 24, 36, 48, 64, 72,
] as const;
export type AllowedDuration = (typeof ALLOWED_DURATIONS)[number];
export const MAX_DURATION_HOURS = 72;

export const serviceService = {
  async getAll(page = 0, size = 20): Promise<PagedResponse<Service>> {
    const response = await api.get<PagedResponse<Service>>("/services", {
      page,
      size,
    });
    return response.data;
  },

  async getById(id: number): Promise<Service> {
    const response = await api.get<Service>("/services/" + id);
    return response.data;
  },

  getDurationOptions(service: Service): DurationOption[] {
    const durationLabels: Record<number, string> = {
      1: "1 hour",
      2: "2 hours",
      4: "4 hours",
      6: "6 hours",
      8: "8 hours",
      12: "12 hours",
      16: "16 hours",
      20: "20 hours",
      24: "24 hours",
      36: "36 hours",
      48: "48 hours",
      64: "64 hours",
      72: "72 hours",
    };

    return Object.entries(service.durationData)
      .map(([hours, price]) => ({
        hours: parseInt(hours),
        price: price as number,
        label: durationLabels[parseInt(hours)] || hours + " hours",
      }))
      .sort((a, b) => a.hours - b.hours);
  },

  formatPrice(price: number, currency = "USD"): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(price);
  },

  getLowestPrice(service: Service): number {
    const prices = Object.values(service.durationData);
    return Math.min(...prices);
  },

  isValidDuration(hours: number): hours is AllowedDuration {
    return ALLOWED_DURATIONS.includes(hours as AllowedDuration);
  },
};
